import {
  AddressProofType,
  BuyOptions,
  CategoriesForSell,
  Countries,
  FileFormats,
  FlowType,
  PurposeOfRemittance,
  PurposeToBuy,
  ReasonForReloadCard,
  RelationshipWithReceiver,
} from '../enums';

export const getTransactionType = (transactionType: string) => {
  if (transactionType === FlowType.Sell) {
    return 'SELL';
  }
  if (transactionType === FlowType.SendMoneyAbroad) {
    return 'OUTWARD_REMITTANCE';
  }
  if (transactionType === 'BUY_CARD') {
    return 'Forex Card';
  }
  if (transactionType === 'BUY_CASH') {
    return 'Forex Cash';
  }
  return '';
};

export const validatePincode = (value: string) => {
  const regex = /^[1-9][0-9]{5}$/;
  return regex.test(value);
};

export const getNextFiveDays = () => {
  const daysToDisplay = [];
  const today = new Date();

  while (daysToDisplay.length < 5) {
    const nextDay = new Date(today.getTime() + 24 * 60 * 60 * 1000); // Add 24 hours (1 day)
    if (nextDay.getDay() !== 0) {
      // Exclude Sundays
      const day = nextDay.toLocaleString('en-us', {
        weekday: 'short',
      });
      const date = nextDay.getDate();
      const month = nextDay.toLocaleString('en-us', {
        month: 'short',
      });
      const fullDate = `${nextDay.getFullYear()}-${String(
        nextDay.getMonth() + 1
      ).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;
      daysToDisplay.push({ day, date, month, fullDate });
    }
    today.setTime(nextDay.getTime());
  }

  return daysToDisplay;
};

export const appointmentSlotTimes = [
  '9:30 AM - 11:00 AM',
  '11:00 AM - 12:30 PM',
  '1:30 PM - 3:00 PM',
  '3:00 PM - 5:30 PM',
];

export const getTimeSlotDuration = (timeslot: string): number => {
  const [startTimeString, endTimeString] = timeslot.split(' - ');
  const startTime = new Date(`2000-01-01 ${startTimeString}`);
  const endTime = new Date(`2000-01-01 ${endTimeString}`);
  const durationInMinutes =
    (endTime.getTime() - startTime.getTime()) / (1000 * 60);
  return durationInMinutes;
};

export const formatTransactionType = (transactionType: string) => {
  switch (transactionType) {
    case 'OUTWARD_REMITTANCE':
      return 'Send Money Abroad';
    case 'SELL':
      return 'Sell Forex Currency';
    case 'CARD_UNLOAD':
      return 'Unload Forex Card';
    case 'CARD_RELOAD':
      return 'Reload Forex Card';
    case 'BUY_CASH':
      return 'Forex Cash';
    case 'BUY_CARD':
      return 'Forex Card';
    case 'BUY':
      return 'Buy Forex';
    case 'CURRENCY_REQUEST':
      return 'Currency Request';
    default:
      return '';
  }
};

export const formatStringToTitleCase = (inputString: string) => {
  if (!inputString) return '';
  const words = inputString.split('_');
  const transformedWords = words.map((word) => {
    if (word.length > 0) {
      const firstLetter = word.charAt(0).toUpperCase();
      const restOfWord = word.slice(1).toLowerCase();
      return firstLetter + restOfWord;
    }
    return '';
  });
  const transformedString = transformedWords.join(' ');
  return transformedString;
};

export const formatDocumentType = (str: string) => {
  if (str === 'PAN') return str;
  if (str === 'PHOTO') return 'Passport Size Photo';
  if (str === 'HOSPITAL') return 'Letter from Hospital';
  if (str === 'UNIVERSITY_LETTER') return 'Letter from University';
  if (str === 'NEFT_RTGS') return 'NEFT/RTGS';
  if (str === 'NEW') return 'New Trip';
  if (str === 'EXISTING') return 'Existing Trip';
  // const words = str.split('_');
  // const formattedWords = words.map(
  //   (word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  // );
  // return formattedWords.join(' ');
  return formatStringToTitleCase(str);
};

export const formatOrderTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);

  const formattedTimestamp = date
    .toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    })
    .toUpperCase();

  return formattedTimestamp;
};

export const purposeOptions = Object.entries(PurposeOfRemittance).map(
  ([key, value]) => ({
    label: key,
    value,
  })
);

export const relationshipOptions = Object.entries(RelationshipWithReceiver).map(
  ([key, value]) => ({
    label: key,
    value,
  })
);

export const addressProofOptions = Object.entries(AddressProofType).map(
  ([key, value]) => ({
    label: key,
    value,
  })
);

export const purposeBuyOptions = Object.entries(PurposeToBuy).map(
  ([key, value]) => ({
    label: key,
    value,
  })
);

export const buyOptions = Object.entries(BuyOptions).map(([key, value]) => ({
  label: key,
  value,
}));

export const sellOptions = Object.entries(CategoriesForSell).map(
  ([key, value]) => ({
    label: key,
    value,
  })
);

export const reloadCardOptions = Object.entries(ReasonForReloadCard).map(
  ([key, value]) => ({
    label: key,
    value,
  })
);

export const countryOptions = Object.entries(Countries).map(([key, value]) => ({
  label: key,
  value,
}));

export const allowedFileTypes = ['application/pdf', 'image/png', 'image/jpeg'];

export const isDocumentTwoSided = (documentType: string) => {
  return [
    AddressProofType['Aadhaar Card'],
    AddressProofType.Passport,
    AddressProofType['Driving License'],
    AddressProofType['Voter ID'],
  ].includes(documentType as AddressProofType);
};

export const getFileFormat = (fileType: string) => {
  if (fileType === 'application/pdf') {
    return FileFormats.Pdf;
  }
  if (fileType === 'image/jpeg') {
    return FileFormats.Jpeg;
  }
  if (fileType === 'image/jpg') {
    return FileFormats.Jpg;
  }
  if (fileType === 'image/png') {
    return FileFormats.Png;
  }
  return '';
};

export const downloadFile = (file: File | null) => {
  if (file) {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    link.click();
    URL.revokeObjectURL(url);
  }
};

export const fileToBinary = (file: File): Promise<Uint8Array> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      const arrayBuffer = reader.result as ArrayBuffer;
      const binaryData = new Uint8Array(arrayBuffer);
      resolve(binaryData);
    };

    reader.onerror = () => {
      reject(new Error('Error reading file as binary'));
    };

    reader.readAsArrayBuffer(file);
  });
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const base64Data = (reader.result as string).split(',')[1];
      resolve(base64Data);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
};

export const sendMoneyAbroadVkycSteps = [
  'Complete your video KYC via the link sent to your email id/mobile number.',
  'Upon successful completion, we will process your order. (If Video KYC is unsuccessful, please contact support so we can schedule an appointment at a nearby Muthoot FinCorp Branch.)',
  'Money will be credited to receiver within 48 hrs*.',
];

export const sendMoneyAbroadOnlineSteps = [
  'Visit branch at the alotted time slot',
  'Share your order ID with the branch manager',
  'Verify your original documents at the branch ',
  'Once verified, your order will be processed at the branch',
  'Money will be credited in 48 hrs*',
];

export const sendMoneyAbroadBranchSteps = [
  'Visit branch at the alotted time slot',
  'Share your order ID with the branch manager',
  'Verify your original documents at the branch ',
  'Make your payment at the branch',
  'Once paid, your order will be processed',
  'Money will be credited in 48 hrs*',
];

export const buyForexVkycSteps = [
  'Complete your video KYC via the link sent to your email id/mobile number.',
  'Upon successful completion, visit the branch to collect your forex order (If Video KYC is unsuccessful, please contact support so we can schedule an appointment at a nearby Muthoot FinCorp Branch.',
];

export const buyForexBranchSteps = [
  'Visit branch at the alotted time slot',
  'Share your order ID with the branch manager',
  'Verify your original documents at the branch ',
  'Pay for your Forex order at branch',
  'Collect your cash or multi-currency card',
];

export const buyForexOnlineSteps = [
  'Visit branch at the alotted time slot',
  'Share your order ID with the branch manager',
  'Verify your original documents at the branch ',
  'Collect your cash or multi-currency card',
];

export const sellForexBranchSteps = [
  'Visit branch at the alotted time slot',
  'Share your order ID with the branch manager',
  'Verify your original documents at the branch ',
  'Currency verification and counterfeit checks will be done',
  'Once verified payment will be processed to your bank a/c',
];

export const sellForexOnlineSteps = [
  'Visit branch at the alotted time slot',
  'Share your order ID with the branch manager',
  'Verify your original documents at the branch ',
  'Currency verification and counterfeit checks will be done',
  'Once verified payment will be processed to your bank a/c',
];

export const unloadForexVkycSteps = [
  'Complete your video KYC via the link sent to your email id/mobile number.',
  'Post Successful Video KYC unload card process will be initiated and you will be notified once the amount is settled into your bank account(If Video KYC is unsuccessful, please contact support so we can schedule an appointment at a nearby Muthoot FinCorp Branch).',
];

export const unloadForexBranchSteps = [
  'Visit branch at the alotted time slot',
  'Share your order ID with the branch manager',
  'Verify your original documents at the branch ',
  'Once verified payment will be processed to your bank a/c',
];

export const unloadForexOnlineSteps = [
  'Visit branch at the alotted time slot',
  'Share your order ID with the branch manager',
  'Verify your original documents at the branch ',
  'Once verified payment will be processed to your bank a/c',
];

export const reloadForexVkycSteps = [
  'Uploaded visa copy will be verified.',
  'Post successful visa copy verification, the process to reload your forex card will be initiated',
];

export const reloadForexBankSteps = [
  'Visit branch at the alotted time slot',
  'Share your order ID with the branch manager',
  'Verify your original documents at the branch ',
  'Pay for your Forex order at branch',
  'Get your forex card reloaded with the requested currency',
];

export const reloadForexOnlineSteps = [
  'Visit branch at the alotted time slot',
  'Share your order ID with branch manager',
  'Verify your original documents at the branch ',
  'Get your forex card reloaded with the requested currency',
];

export const sendMoneyAbroadSteps = [
  'Order Request received',
  'Documents Approved',
  'Order Processed',
  'Amount Transferred',
];

export const buySteps = [
  'Order Request received',
  'Documents Approved',
  'Order Processed',
  'Product Delivered',
];

export const sellSteps = [
  'Order Request received',
  'Documents Approved',
  'Order Processed',
  'INR Amount Settled',
];

export const unloadCardSteps = [
  'Order Request received',
  'Documents Approved',
  'Order Processed',
  'INR Amount Settled',
];

export const currencyRequestSteps = [
  'Our team will get in touch with you within 24 working hours*, to confirm the availability of your requested currency.',
];

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate();
  let suffix = '';
  switch (day) {
    case 1:
    case 21:
    case 31:
      suffix = 'st';
      break;
    case 2:
    case 22:
      suffix = 'nd';
      break;
    case 3:
    case 23:
      suffix = 'rd';
      break;
    default:
      suffix = 'th';
  }
  const month = date.toLocaleString('default', { month: 'short' });
  const year = date.getFullYear().toString().substr(-2);
  let hours = date.getHours();
  let ampm = 'AM';
  if (hours === 0) {
    hours = 12;
  } else if (hours === 12) {
    ampm = 'PM';
  } else if (hours > 12) {
    hours -= 12;
    ampm = 'PM';
  }
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day}${suffix} ${month} '${year}, ${hours}:${minutes} ${ampm}`;
};

export const formatDateToDMY = (dateString: string) => {
  const inputDate = new Date(dateString);
  const year = inputDate.getFullYear();
  const month = String(inputDate.getMonth() + 1).padStart(2, '0');
  const day = String(inputDate.getDate()).padStart(2, '0');
  const formattedDate = `${day}/${month}/${year}`;
  return formattedDate;
};

export const formatFxOrderStatus = (status: string) => {
  switch (status) {
    case 'PENDING':
      return 'Pending';
    case 'APPROVED':
      return 'Approved';
    case 'REJECTED':
      return 'Rejected';
    case 'REFUNDED':
      return 'Refunded';
    case 'PROCESSED':
      return 'Processed';
    case 'TRANSFER_INITIATED':
      return 'Transfer Initiated';
    case 'PAYMENT_PENDING':
      return 'Pending';
    case 'PAYMENT_OFFLINE':
      return 'Offline';
    case 'OFFLINE':
      return 'Offline';
    case 'INITIATED':
      return 'Initiated';
    case 'PAID':
      return 'Paid';
    case 'FAILED':
      return 'Failed';
    default:
      return '';
  }
};

export const fxOrderStatusColor = (status: string) => {
  switch (status) {
    case 'PENDING':
      return 'text--orange';
    case 'APPROVED':
      return 'text--green';
    case 'REJECTED':
      return 'text--red';
    case 'REFUNDED':
      return 'text--ocean-blue';
    case 'PROCESSED':
      return 'text--green';
    case 'TRANSFER_INITIATED':
      return 'text--green';
    case 'PAYMENT_PENDING':
      return 'text--orange';
    case 'PAYMENT_OFFLINE':
      return 'text--blue';
    case 'OFFLINE':
      return 'text--blue';
    case 'INITIATED':
      return 'text--orange';
    case 'PAID':
      return 'text--green';
    case 'FAILED':
      return 'text--red';
    default:
      return 'text--green';
  }
};

export const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const accountNumberRegex = /^[0-9]{9,18}$/;
export const textRegex = /^(?! )[A-Za-z ]+(?<! )$/;
export const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
export const alphaNumericRegex = /^[a-zA-Z0-9]*$/;
